import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import styles from './Plot.module.scss';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Navbar from './Navbar';

const Plot = () => {
  const { plotId } = useParams();
  const [plot, setPlot] = useState(null);
  const [individualMessages, setIndividualMessages] = useState([]);
  const [groupMessages, setGroupMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');

  // Fetch plot, users, and groups on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch plot
        const plotRes = await axios.get(`${process.env.REACT_APP_URL}/plots/${plotId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPlot(plotRes.data);

        // Fetch users and groups concurrently
        const [usersRes, groupsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_URL}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_URL}/groups`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setUsers(usersRes.data);
        setGroups(groupsRes.data);

        setError('');
      } catch (err) {
        console.error('Failed to fetch plot, users, or groups', err);
        setError('Failed to fetch plot, users, or groups');
      }
    };

    fetchData();
  }, [plotId, token]);

  // Fetch messages only after the plot is fetched
  useEffect(() => {
    const fetchMessages = async () => {
      if (plot) {
        try {
          const res = await axios.get(`${process.env.REACT_APP_URL}/messages/plot/${plot.plotId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setIndividualMessages(res.data.individualMessages);
          setGroupMessages(res.data.groupMessages);
          setError('');
        } catch (err) {
          console.error('Failed to fetch associated messages', err);
          setError('Failed to fetch messages');
        }
      }
    };

    fetchMessages();
  }, [plot, token]);

  const translateId = (id, isGroup = false) => {
    if (isGroup) {
      const group = groups.find((group) => group.id === id);
      return group ? group.name : 'Unknown Group';
    } else {
      const user = users.find((user) => user.id === id);
      return user ? user.username : 'Unknown User';
    }
  };

  if (error) return <p>{error}</p>;
  if (!plot) return <p>Loading...</p>;

  return (
    <div className="App">
      <Navbar />
      <div className="allContent">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs="12" lg="12" className='mt-1'>
              <div className={styles.plotContainer}>
                <h2>{plot.name}</h2>
                {plot.image && (
                  <img src={`${process.env.REACT_APP_URL}uploads/${plot.image}`} alt={plot.name} className={styles.plotImage} />
                )}
                <p>{plot.description}</p>

                <table className={styles.messagesTable}>
                  <thead>
                    <tr className={styles.tableRow}>
                      <th className={styles.tableHeader}>Individual Messages</th>
                      <th className={styles.tableHeader}>Sent by</th>
                      <th className={styles.tableHeader}>Message Content</th>
                    </tr>
                  </thead>
                  <tbody>
                    {individualMessages.length > 0 ? (
                      individualMessages.map((message) => (
                        <tr key={message.id} className={styles.tableRow}>
                          <td className={styles.tableCell}>Individual Message</td>
                          <td className={styles.tableCell}>{translateId(message.senderId)}</td>
                          <td className={styles.tableCell}>{message.content}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className={styles.tableRow}>
                        <td className={styles.tableCell} colSpan="3">
                          No individual messages associated with this plot.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  <thead>
                    <tr className={styles.tableRow}>
                      <th className={styles.tableHeader}>Group Messages</th>
                      <th className={styles.tableHeader}>Sent by</th>
                      <th className={styles.tableHeader}>Message Content</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupMessages.length > 0 ? (
                      groupMessages.map((message) => (
                        <tr key={message.id} className={styles.tableRow}>
                          <td className={styles.tableCell}>Group Message</td>
                          <td className={styles.tableCell}>
                            {translateId(message.groupId, true)} by {translateId(message.senderId)}
                          </td>
                          <td className={styles.tableCell}>{message.content}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className={styles.tableRow}>
                        <td className={styles.tableCell} colSpan="3">
                          No group messages associated with this plot.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default Plot;
